import React from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { keepOnlyDigits } from './utils';
import ErrorLabel from './ErrorLabel';
import Tooltip from './Tooltip';
import amexExample from '../resources/amexExample.jpg';
import genericExample from '../resources/genericExample.jpg';

const CvxInput = ({ readOnly, onChange, length, forceDirty }) => {
    const { t } = useTranslation();

    const [isValid, setValid] = React.useState(false);
    const [value, setValue] = React.useState('');
    const [isDirty, setDirty] = React.useState(false);
    const [isFocused, setFocused] = React.useState(false);

    const minLength = Math.min(...length);
    const maxLength = Math.max(...length);

    const checkIsValid = React.useCallback(
        value => {
            return (
                maxLength === 0 ||
                (value.length >= minLength && value.length <= maxLength)
            );
        },
        [minLength, maxLength]
    );

    React.useEffect(() => {
        // react to `length` change
        const valid = checkIsValid(value);
        if (isValid !== valid) {
            onChange({ value, isValid: valid });
            setValid(valid);
        }
    }, [checkIsValid, isValid, onChange, value]);

    return maxLength > 0 || length.length === 0 ? (
        <div className="wz-lyriapay-payline-container-cvc">
            <div
                className={`wz-lyriapay-payline-formblock wz-lyriapay-payline-formblock-cvc${
                    (isDirty || forceDirty) && !isValid
                        ? ' wz-lyriapay-payline-formblock-error'
                        : ''
                }`}
            >
                <label htmlFor="cvc">
                    {t('cvcLabel')}
                    <Tooltip alt={t('cvcTooltipTrigger')}>
                        <Trans i18nKey="cvcTooltip" />
                        <div className="wz-lyriapay-payline-tooltip-imagecontainer">
                            <div className="wz-lyriapay-payline-tooltip-imagecontainer-row">
                                <img
                                    src={genericExample}
                                    alt={t('genericExample')}
                                />
                            </div>
                            <div className="wz-lyriapay-payline-tooltip-imagecontainer-row">
                                <img src={amexExample} alt={t('amexExample')} />
                            </div>
                        </div>
                    </Tooltip>
                </label>
                <input
                    id="cvc"
                    name="cvc"
                    required
                    autoComplete="cc-csc"
                    inputMode="numeric"
                    value={value}
                    disabled={readOnly}
                    maxLength={maxLength}
                    onBlur={() => setFocused(false)}
                    onFocus={() => setFocused(true)}
                    onChange={e => {
                        const value = keepOnlyDigits(e.target.value);
                        const valid = checkIsValid(value);
                        setDirty(true);
                        setValue(value);
                        setValid(valid);
                        onChange({
                            value: value,
                            isValid: valid
                        });
                    }}
                    aria-invalid={
                        (isDirty || forceDirty) && !isValid && !isFocused
                    }
                    aria-required
                />
            </div>
            <ErrorLabel
                content={
                    (isDirty || forceDirty) && !isValid && !isFocused
                        ? value === ''
                            ? t('cvcEmpty')
                            : t('cvcInvalid')
                        : null
                }
            />
        </div>
    ) : null;
};

CvxInput.propTypes = {
    readOnly: PropTypes.bool.isRequired,
    length: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    forceDirty: PropTypes.bool
};

export default CvxInput;
